import tvgConf from "@tvg/conf";
import requester from "../../requester";
import getProtocol from "../../protocolSetter";
import { serviceResponse } from "../../types";
import type {
  BraintreeCCClientTokenResponse,
  BraintreeCCDepositResponse,
  BraintreeCCDepositFeeResponse,
  BraintreePaymentMethodListResponse,
  BraintreeCardListResponse,
  BraintreeWithdrawalRequest,
  BraintreeWithdrawalResponse
} from "./types";

const brtService: string = "service.brt";

const getBraintreeCCClientToken = (
  accountNumber: string
): Promise<serviceResponse<BraintreeCCClientTokenResponse>> =>
  requester()({
    method: "GET",
    url: `${getProtocol()}${tvgConf().config(
      brtService
    )}/users/${accountNumber}/client-token`,
    headers: { "x-tvg-context": tvgConf().context() },
    withCredentials: true
  });

const finishBraintreeCCDeposit = (
  accountNumber: string,
  paymentMethodNonce: string,
  amount: number,
  deviceData: string,
  storeInVaultOnSuccess: boolean = true
) => {
  if (!accountNumber || !paymentMethodNonce || !deviceData) {
    return Promise.reject({
      response: { data: { code: "MISSING_REQUIRED_PARAMS" } }
    });
  }

  if (amount <= 0) {
    return Promise.reject({
      response: { data: { code: "INVALID_AMOUNT" } }
    });
  }

  return requester()({
    method: "POST",
    url: `${getProtocol()}${tvgConf().config(
      brtService
    )}/users/${accountNumber}/deposit/card`,
    headers: { "x-tvg-context": tvgConf().context() },
    withCredentials: true,
    data: {
      paymentMethodNonce,
      amount,
      deviceData,
      storeInVaultOnSuccess
    }
  }) as Promise<serviceResponse<BraintreeCCDepositResponse>>;
};

const finishBraintreeCCVaultDeposit = (
  accountNumber: string,
  paymentMethodToken: string,
  amount: number,
  deviceData: string
) => {
  if (!accountNumber || !paymentMethodToken || !deviceData) {
    return Promise.reject({
      response: { data: { code: "MISSING_REQUIRED_PARAMS" } }
    });
  }

  if (amount <= 0) {
    return Promise.reject({
      response: { data: { code: "INVALID_AMOUNT" } }
    });
  }

  return requester()({
    method: "POST",
    url: `${getProtocol()}${tvgConf().config(
      brtService
    )}/users/${accountNumber}/deposit/vault`,
    headers: { "x-tvg-context": tvgConf().context() },
    withCredentials: true,
    data: {
      paymentMethodToken,
      amount,
      deviceData
    }
  }) as Promise<serviceResponse<BraintreeCCDepositResponse>>;
};

const getBraintreeCCDepositFee = (
  accountNumber: string,
  paymentType: string,
  amount: number
): Promise<serviceResponse<BraintreeCCDepositFeeResponse>> =>
  requester()({
    method: "GET",
    url: `${getProtocol()}${tvgConf().config(
      brtService
    )}/users/${accountNumber}/deposit/fees`,
    headers: { "x-tvg-context": tvgConf().context() },
    withCredentials: true,
    params: { paymentType, amount }
  });

const getBraintreePaymentMethods = (
  accountNumber: string
): Promise<serviceResponse<BraintreePaymentMethodListResponse>> =>
  requester()({
    method: "GET",
    url: `${getProtocol()}${tvgConf().config(
      brtService
    )}/users/${accountNumber}/payment-methods`,
    headers: { "x-tvg-context": tvgConf().context() },
    withCredentials: true
  });

const getBraintreeCards = (
  accountNumber: string
): Promise<serviceResponse<BraintreeCardListResponse>> =>
  requester()({
    method: "GET",
    url: `${getProtocol()}${tvgConf().config(
      brtService
    )}/users/${accountNumber}/payment-methods/cards`,
    headers: { "x-tvg-context": tvgConf().context() },
    withCredentials: true
  });

const deleteBraintreePaymentMethod = (
  accountNumber: string,
  paymentMethodToken: string
): Promise<serviceResponse> =>
  requester()({
    method: "DELETE",
    url: `${getProtocol()}${tvgConf().config(
      brtService
    )}/users/${accountNumber}/payment-methods/${paymentMethodToken}`,
    headers: { "x-tvg-context": tvgConf().context() },
    withCredentials: true
  });

const makeBraintreeCardWithdrawal = (
  accountNumber: string,
  data: BraintreeWithdrawalRequest,
  bypassFee: boolean = false,
  overrideWithdrawalBalance: boolean = false
): Promise<serviceResponse<BraintreeWithdrawalResponse>> =>
  requester()({
    method: "POST",
    url: `${getProtocol()}${tvgConf().config(
      brtService
    )}/users/${accountNumber}/withdrawal/card`,
    headers: { "x-tvg-context": tvgConf().context() },
    withCredentials: true,
    params: { bypassFee, overrideWithdrawalBalance },
    data
  });

export {
  getBraintreeCCClientToken,
  finishBraintreeCCDeposit,
  finishBraintreeCCVaultDeposit,
  getBraintreeCCDepositFee,
  getBraintreePaymentMethods,
  getBraintreeCards,
  deleteBraintreePaymentMethod,
  makeBraintreeCardWithdrawal
};
